import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Plus, Trash } from "lucide-react";
import { FormControl, MenuItem, Select } from "@mui/material";
import Modal from "../common/CommonModal";
import CreditCustomerList from "../common/CreditCustomerList";
import useApi from "../../hooks/useApi";

const OtherCostSection = ({
  otherCosts,
  setOtherCosts,
  purchaseItems,
  BASE_URL,
  COMPANY_CODE,
  invoiceNoFromState,
}) => {
  const [currencies, setCurrencies] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [ccModalOpen, setCcModalOpen] = useState(false);
  const [ccDefaultType, setCcDefaultType] = useState("Asset");

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [suggestionPosition, setSuggestionPosition] = useState(null);
  const [activeCell, setActiveCell] = useState({ rowId: null, field: null });

  const inputRefs = useRef({});

  const { request: requestCurrencyList } = useApi();
  useEffect(() => {
    const fetchCurrencyList = async () => {
      const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/CURRENCY/-/-`;
      try {
        const response = await requestCurrencyList({
          url,
          method: "GET",
          timeout: 10000,
        });

        // Normalize response: map fields to generic structure
        const formatted = (response || []).map((c) => ({
          code: c.curno, // e.g. "AED"
          label: c.cur, // e.g. "DHS"
          rate: c.rate, // e.g. 1
        }));

        setCurrencies(formatted);
        console.log("Formatted Currencies", formatted);
      } catch (err) {
        console.error("Error loading data:", err);
        setCurrencies([]);
      }
    };

    fetchCurrencyList();
  }, []);
  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "";
  const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;
  // console.log(" VATPERCENTAGE", VATPERCENTAGE)
  // Create new row
  const createNewRow = useCallback(() => {
    const defaultCurrency = currencies.length > 0 ? currencies[0].code : "";
    const defaultRate =
      currencies.length > 0 ? Number(currencies[0].rate) || 1 : 1;
    return {
      id: Date.now(),
      drAcc: "",
      drDescription: "",
      ref: "",
      amount: "0.00",
      vat5Enabled: false,
      vatPercent: VATPERCENTAGE,
      vatAmt: "0.00",
      total: "0.00",
      remarks: "",
      crAcc: "",
      crDescription: "",
      rev: "N",
      cur: defaultCurrency,
      rate: defaultRate,
      fcAmount: "0.00",
      lastEdited: null,
    };
  }, [currencies]);

  // Initialize with one row if empty and handle initial focus
  useEffect(() => {
    if (currencies.length > 0 && otherCosts.length === 0) {
      const initialRow = createNewRow();
      setOtherCosts([initialRow]);

      // Focus first row drAcc
      setTimeout(() => {
        const inputKey = `${initialRow.id}-drAcc`;
        inputRefs.current[inputKey]?.focus();
      }, 300);
    }
  }, [currencies.length, otherCosts.length, createNewRow, setOtherCosts]);

  const calculateOtherCostValues = useCallback((cost, currencies) => {
    const curCode = cost.cur;
    const rate = Number(cost.rate) || 1;
    const vatEnabled = cost.vat5Enabled !== false;
    const vatPercent = vatEnabled ? VATPERCENTAGE : 0;

    let amount, fcAmount, baseAmount;

    // Direction depends on last edited field
    if (cost.lastEdited === "fcAmount") {
      fcAmount = Number(cost.fcAmount) || 0;
      baseAmount = curCode !== "AED" ? fcAmount * rate : fcAmount;
      amount = baseAmount;
    } else if (cost.lastEdited === "amount") {
      amount = Number(cost.amount) || 0;
      baseAmount = amount;
      fcAmount = curCode !== "AED" ? amount / rate : amount;
    } else {
      // For any change like currency or rate update
      amount = Number(cost.amount) || 0;
      baseAmount = amount;
      fcAmount = curCode !== "AED" ? amount / rate : amount;
    }

    const vatAmt = vatEnabled ? (baseAmount * vatPercent) / 100 : 0;
    const lineTotal = baseAmount + vatAmt;

    return {
      ...cost,
      fcAmount: fcAmount.toFixed(2),
      amount: baseAmount.toFixed(2),
      vatAmt: vatAmt.toFixed(2),
      total: lineTotal.toFixed(2),
      baseAmount: baseAmount.toFixed(2),
      rate: rate,
    };
  }, []);

  // Add new row
  const addOtherCostRow = useCallback(() => {
    const newRow = createNewRow();
    setOtherCosts((prev) => [...prev, newRow]);

    // Focus drAcc of the new row
    setTimeout(() => {
      const inputKey = `${newRow.id}-drAcc`;
      inputRefs.current[inputKey]?.focus();
    }, 100);
  }, [createNewRow, setOtherCosts]);

  // Update cost field
  const updateOtherCost = useCallback(
    (id, field, value) => {
      setOtherCosts((prev) =>
        prev.map((cost) => {
          if (cost.id === id) {
            let updated = { ...cost, [field]: value, lastEdited: field };

            if (field === "cur") {
              const selectedCurrency = currencies.find((c) => c.code === value);
              if (selectedCurrency) {
                updated.rate = selectedCurrency.rate;
              }
              updated.lastEdited = "cur";
              return calculateOtherCostValues(updated, currencies);
            }

            if (field === "rate") {
              updated.lastEdited = "rate";
              return calculateOtherCostValues(updated, currencies);
            }

            // Don't recalculate while typing in amount fields
            if (field === "amount" || field === "fcAmount") {
              return updated;
            }

            return calculateOtherCostValues(updated, currencies);
          }
          return cost;
        }),
      );
    },
    [calculateOtherCostValues, currencies, setOtherCosts],
  );

  // Handle amount blur (finalize calculation)
  const handleAmountBlur = useCallback(
    (rowId, field) => {
      setOtherCosts((prev) =>
        prev.map((cost) => {
          if (cost.id === rowId) {
            return calculateOtherCostValues(
              { ...cost, lastEdited: field },
              currencies,
            );
          }
          return cost;
        }),
      );
    },
    [calculateOtherCostValues, currencies, setOtherCosts],
  );

  // Select account from suggestions
  const selectAccountSuggestion = useCallback(
    (account) => {
      if (!activeCell.rowId || !activeCell.field) return;

      const { rowId, field } = activeCell;
      const descField = field === "drAcc" ? "drDescription" : "crDescription";

      setOtherCosts((prev) =>
        prev.map((cost) =>
          cost.id === rowId
            ? {
                ...cost,
                [field]: account.account,
                [descField]: account.description,
              }
            : cost,
        ),
      );

      // Close suggestions
      setShowSuggestions(false);
      setSuggestions([]);
      setActiveCell({ rowId: null, field: null });
      setSelectedSuggestionIndex(-1);
      setSuggestionPosition(null);

      // Focus next field
      setTimeout(() => {
        const nextField = field === "drAcc" ? "ref" : "crDescription";
        const inputKey = `${rowId}-${nextField}`;
        inputRefs.current[inputKey]?.focus();
      }, 0);
    },
    [activeCell, setOtherCosts],
  );

  // Handle customer select from modal
  const handleCustomerSelect = useCallback(
    (account) => {
      if (!account || !activeCell.rowId || !activeCell.field) {
        setCcModalOpen(false);
        return;
      }

      const { rowId, field } = activeCell;
      const descField = field === "drAcc" ? "drDescription" : "crDescription";

      setOtherCosts((prev) =>
        prev.map((cost) =>
          cost.id === rowId
            ? {
                ...cost,
                [field]: account.account || account.ACCOUNT,
                [descField]:
                  account.description?.trim() || account.DESCRIPTION?.trim(),
              }
            : cost,
        ),
      );

      setCcModalOpen(false);

      // Focus next field
      setTimeout(() => {
        const nextField = field === "drAcc" ? "ref" : "crDescription";
        const inputKey = `${rowId}-${nextField}`;
        inputRefs.current[inputKey]?.focus();
      }, 100);

      setActiveCell({ rowId: null, field: null });
    },
    [activeCell, setOtherCosts],
  );

  // Delete selected rows
  const deleteSelectedRows = useCallback(() => {
    setOtherCosts((prev) =>
      prev.filter((cost) => !selectedRowIds.includes(cost.id)),
    );
    setSelectedRowIds([]);
  }, [selectedRowIds, setOtherCosts]);

  // Calculate totals
  const calculateTotals = useMemo(() => {
    const validCosts = otherCosts.filter(
      (cost) => cost.drAcc?.trim() && cost.crAcc?.trim(),
    );

    // Only sum the base amounts for other costs (without VAT) - used for document calculations
    const totalOtherCost = validCosts.reduce(
      (sum, cost) => sum + (Number(cost.baseAmount) || 0),
      0,
    );

    // Calculate line-wise VAT totals (for display only)
    const totalVat = validCosts.reduce(
      (sum, cost) => sum + (Number(cost.vatAmt) || 0),
      0,
    );

    // Calculate total with VAT (for display in other cost section)
    const totalWithVat = validCosts.reduce(
      (sum, cost) => sum + (Number(cost.total) || 0),
      0,
    );

    return {
      totalOtherCost: totalOtherCost.toFixed(2), // Base amount only - used for document calculations
      totalVat: totalVat.toFixed(2), // VAT amount only - for display
      totalWithVat: totalWithVat.toFixed(2), // Total including VAT - for display
    };
  }, [otherCosts]);

  // Render editable cell
  const renderEditableCell = useCallback(
    (params, field, type = "text") => {
      const rowId = params.row.id;
      const isAccountField = field === "drAcc" || field === "crAcc";
      const inputKey = `${rowId}-${field}`;

      // Use text for numeric fields so .select() works reliably in browsers
      const displayType = type === "number" ? "text" : type;

      return (
        <div className="relative w-full">
          <input
            ref={(el) => (inputRefs.current[inputKey] = el)}
            type={displayType}
            data-field-type={isAccountField ? "account" : ""}
            value={params.value || ""}
            onChange={(e) => {
              const value = e.target.value;
              updateOtherCost(rowId, field, value);
            }}
            onBlur={() => {
              if (field === "amount" || field === "fcAmount") {
                handleAmountBlur(rowId, field);
              }
            }}
            onKeyDown={(e) => {
              // Prevent DataGrid from capturing space key
              if (e.key === " " && type !== "number") {
                e.stopPropagation();
              }

              // F2 to open accounts modal or add new row
              if (e.key === "F2") {
                if (isAccountField) {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.nativeEvent) {
                    e.nativeEvent.stopImmediatePropagation();
                  }

                  setCcDefaultType(field === "drAcc" ? "Asset" : "Suppliers");
                  setActiveCell({ rowId, field });
                  setCcModalOpen(true);
                  return;
                } else if (field === "crDescription") {
                  e.preventDefault();
                  addOtherCostRow();
                  return;
                }
              }

              // Handle suggestion navigation
              const isActiveSuggestion =
                isAccountField &&
                showSuggestions &&
                activeCell.rowId === rowId &&
                activeCell.field === field;

              if (isActiveSuggestion) {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setSelectedSuggestionIndex((i) =>
                    Math.min(i + 1, suggestions.length - 1),
                  );
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setSelectedSuggestionIndex((i) => Math.max(i - 1, 0));
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setShowSuggestions(false);
                  setSelectedSuggestionIndex(-1);
                }
              }

              if (e.key === "Enter") {
                if (isActiveSuggestion) {
                  e.preventDefault();
                  const selected =
                    suggestions[selectedSuggestionIndex] || suggestions[0];
                  if (selected) selectAccountSuggestion(selected);
                } else if (field === "crDescription") {
                  e.preventDefault();
                  addOtherCostRow();
                }
              }
            }}
            className={`w-full h-full px-2 py-1 bg-transparent ${
              !isAccountField ? "text-right" : ""
            }`}
            autoComplete="off"
          />
        </div>
      );
    },
    [
      showSuggestions,
      suggestions,
      activeCell,
      selectedSuggestionIndex,
      updateOtherCost,
      // handleAccountSearch,
      selectAccountSuggestion,
      handleAmountBlur,
    ],
  );

  const renderSelectCell = useCallback(
    (params, field, options) => {
      // Get all possible values from options
      const optionValues = options.map(
        (option) => option.code || option.value || "",
      );
      // If the current value is not in options, fallback to ""
      const value = optionValues.includes(params.value) ? params.value : "";

      return (
        <FormControl size="small" fullWidth variant="standard">
          <Select
            value={value}
            onChange={(e) =>
              updateOtherCost(params.row.id, field, e.target.value)
            }
            disableUnderline
            sx={{
              "& .MuiSelect-select": {
                padding: "4px 8px",
                fontSize: "0.875rem",
              },
            }}
          >
            {options.map((option) => (
              <MenuItem
                key={option.code || option.value}
                value={option.code || option.value}
              >
                {option.code || option.label}
              </MenuItem>
            ))}
            {/* Fallback option if value is not in options */}
            {!optionValues.includes(params.value) && params.value && (
              <MenuItem value={params.value}>{params.value}</MenuItem>
            )}
          </Select>
        </FormControl>
      );
    },
    [updateOtherCost],
  );

  // Render VAT toggle
  const renderVatToggle = useCallback(
    (params) => {
      const isChecked = params.value !== false;
      return (
        <div className="flex items-center justify-center h-full">
          <button
            onClick={() =>
              updateOtherCost(params.row.id, "vat5Enabled", !isChecked)
            }
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              isChecked ? "bg-blue-500 text-white" : "bg-gray-300 text-gray-700"
            }`}
          >
            {isChecked ? `${VATPERCENTAGE}%` : "0%"}
          </button>
        </div>
      );
    },
    [updateOtherCost],
  );

  // Column definitions
  const columns = useMemo(
    () => [
      {
        field: "drAcc",
        headerName: "Dr_Ac",
        width: 120,
        renderCell: (params) => renderEditableCell(params, "drAcc"),
      },
      {
        field: "drDescription",
        headerName: "D-Description",
        width: 180,
        renderCell: (params) => renderEditableCell(params, "drDescription"),
      },
      {
        field: "ref",
        headerName: "Ref",
        width: 100,
        renderCell: (params) => renderEditableCell(params, "ref"),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 100,
        renderCell: (params) => renderEditableCell(params, "amount", "number"),
      },
      {
        field: "vat5Enabled",
        headerName: "Vat%",
        width: 80,
        renderCell: renderVatToggle,
      },
      {
        field: "vatAmt",
        headerName: "VatAmt",
        width: 100,
        renderCell: (params) => (
          <div className="w-full px-2 py-1 text-right bg-blue-50">
            {params.value}
          </div>
        ),
      },
      {
        field: "total",
        headerName: "Total",
        width: 100,
        renderCell: (params) => (
          <div className="w-full px-2 py-1 text-right bg-green-50 font-medium">
            {params.value}
          </div>
        ),
      },
      {
        field: "remarks",
        headerName: "Remarks",
        width: 150,
        renderCell: (params) => renderEditableCell(params, "remarks"),
      },
      {
        field: "crAcc",
        headerName: "Cr_Ac",
        width: 120,
        renderCell: (params) => renderEditableCell(params, "crAcc"),
      },
      {
        field: "crDescription",
        headerName: "C-Description",
        width: 180,
        renderCell: (params) => renderEditableCell(params, "crDescription"),
      },
      {
        field: "rev",
        headerName: "Rev",
        width: 60,
        renderCell: (params) =>
          renderSelectCell(params, "rev", [
            { value: "Y", label: "Y" },
            { value: "N", label: "N" },
          ]),
      },
      {
        field: "cur",
        headerName: "Cur",
        width: 80,
        renderCell: (params) => renderSelectCell(params, "cur", currencies),
      },
      {
        field: "rate",
        headerName: "Rate",
        width: 80,
        renderCell: (params) => renderEditableCell(params, "rate", "number"),
      },
      {
        field: "fcAmount",
        headerName: "FC Amount",
        width: 100,
        renderCell: (params) => (
          <div className="w-full px-2 py-1 text-right bg-yellow-50">
            {params.value}
          </div>
        ),
      },
    ],
    [renderEditableCell, renderSelectCell, renderVatToggle, currencies],
  );

  return (
    <div>
      <div className="bg-white rounded-lg border">
        <div className="max-h-[200px] overflow-auto">
          <DataGrid
            rows={otherCosts}
            columns={columns}
            checkboxSelection
            onRowSelectionModelChange={setSelectedRowIds}
            rowSelectionModel={selectedRowIds}
            hideFooter
            disableRowSelectionOnClick
            columnHeaderHeight={45}
            autoHeight
            rowHeight={40}
            headerHeight={38}
            disableColumnMenu
            sx={{
              border: "none",
              fontFamily: "inherit",
              position: "relative",
              fontSize: "0.875rem",
              "& .MuiDataGrid-cell": {
                borderColor: "#e5e7eb",
                padding: 0,
                "&:focus, &:focus-within": {
                  outline: "none",
                  backgroundColor: "inherit",
                },
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f9fafb",
                borderBottom: "1px solid #d1d5db",
              },
              "& .MuiDataGrid-columnHeader": {
                fontWeight: 600,
                color: "#374151",
                backgroundColor: "#e5e7eb",
                borderRight: "1px solid #e5e7eb",
                "&:last-of-type": {
                  borderRight: "none",
                },
              },
              "& .MuiDataGrid-columnSeparator": {
                color: "#d1d5db",
                visibility: "visible",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f3f4f6",
              },
              "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row:focus-within":
                {
                  outline: "2px solid #3b82f6",
                  outlineOffset: "-1px",
                },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "inherit",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#fafafa",
              },
            }}
          />
        </div>

        <div className="p-3 border-t bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <span className="font-medium text-gray-700">
                Total Other Cost:
              </span>
              <span className="ml-2 font-semibold text-gray-900">
                {calculateTotals.totalWithVat}
              </span>
            </div>
            {/* {Number(calculateTotals.totalVat) > 0 && (
              <div>
                <span className="font-medium text-gray-500 text-sm">(Incl. VAT: {calculateTotals.totalVat})</span>
              </div>
            )} */}
          </div>
          <div className="flex gap-2">
            {selectedRowIds.length > 0 && (
              <button
                onClick={deleteSelectedRows}
                className="inline-flex items-center px-3 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
              >
                <Trash className="w-4 h-4 mr-2" />
                Delete Selected ({selectedRowIds.length})
              </button>
            )}
          </div>
          <button
            onClick={addOtherCostRow}
            className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Row
          </button>
        </div>
      </div>

      <Modal
        isOpen={ccModalOpen}
        onClose={() => {
          setCcModalOpen(false);
          setActiveCell({ rowId: null, field: null });
        }}
        title="Select Account"
        size="xxl"
      >
        <div style={{ padding: 8 }}>
          <CreditCustomerList
            handleCustomerSelect={handleCustomerSelect}
            defaultType="All" //Default type changed from cctype to All as per the requirement (DEEPA 25/04/2026)
            showSidebar={true}
          />
        </div>
      </Modal>
    </div>
  );
};

export default OtherCostSection;
